import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useDeferredValue,
} from 'react';
import { flushSync } from 'react-dom';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputLeftElement,
  Box,
  Stack,
  Checkbox,
  Text,
  Button,
  VStack,
  HStack,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react';
import { FaSearch } from 'react-icons/fa';
import PropTypes from 'prop-types';

// Optimized merchant checkbox component to prevent unnecessary re-renders
const MerchantCheckboxItem = React.memo(
  ({ merchant, isChecked, onSelectionChange }) => {
    const handleChange = useCallback(
      (e) => {
        onSelectionChange(merchant, e.target.checked);
      },
      [merchant, onSelectionChange],
    );

    return (
      <Checkbox
        id={`merchant-checkbox-${merchant.merchantId}`}
        isChecked={isChecked}
        onChange={handleChange}
        p="3"
        w="100%"
        borderRadius="0"
        _hover={{ bg: 'gray.50' }}
        colorScheme="blue"
      >
        <Text
          fontSize="0.875rem"
          fontWeight="500"
          isTruncated
          maxW={{ base: '255px', md: '400px' }}
          title={merchant.name}
        >
          {merchant.name}
        </Text>
      </Checkbox>
    );
  },
);

MerchantCheckboxItem.displayName = 'MerchantCheckboxItem';

MerchantCheckboxItem.propTypes = {
  merchant: PropTypes.object.isRequired,
  isChecked: PropTypes.bool.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
};

const GroupManagementModal = ({
  isOpen,
  onClose,
  groupModalData,
  setGroupModalData,
  merchantNameList,
  onSubmit,
  onCancel,
  onDelete,
  isCreatingGroup,
  isUpdatingGroup,
  isDeleting,
  onMerchantSelection,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  // Uncontrolled input ref for group name
  const groupNameRef = useRef(null);

  // Keep input value in sync when modal opens or group data changes
  useEffect(() => {
    if (groupNameRef.current) {
      groupNameRef.current.value = groupModalData.name || '';
    }
  }, [groupModalData.name, isOpen]);

  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure();
  const cancelRef = useRef();

  // Uncontrolled group name: sync to parent on blur
  const handleGroupNameBlur = useCallback(() => {
    const newValue =
      groupNameRef.current && typeof groupNameRef.current.value === 'string'
        ? groupNameRef.current.value
        : '';
    setGroupModalData((prev) => ({
      ...prev,
      name: newValue,
    }));
  }, [setGroupModalData]);

  // Defer search term to keep typing responsive
  const deferredSearchTerm = useDeferredValue(searchTerm);

  // Filter merchants based on search term (memoized for performance)
  const filteredMerchants = useMemo(() => {
    if (!merchantNameList) return [];
    if (!deferredSearchTerm.trim()) return merchantNameList;

    const lowerSearchTerm = deferredSearchTerm.toLowerCase();
    return merchantNameList.filter((merchant) =>
      merchant.name.toLowerCase().includes(lowerSearchTerm),
    );
  }, [merchantNameList, deferredSearchTerm]);

  // Create a Set of selected merchant IDs for fast lookup (memoized)
  const selectedMerchantIds = useMemo(() => {
    return new Set(groupModalData.merchants.map((m) => m.merchantId));
  }, [groupModalData.merchants]);

  // Memoized callback for merchant selection to prevent re-renders
  const handleMerchantSelectionChange = useCallback(
    (merchant, isSelected) => {
      onMerchantSelection(merchant, isSelected);
    },
    [onMerchantSelection],
  );

  // Memoize the rendered merchant list to avoid rebuilding on unrelated renders
  const merchantListContent = useMemo(() => {
    if (!filteredMerchants.length) return null;
    return (
      <Stack spacing="0">
        {filteredMerchants.map((merchant) => (
          <MerchantCheckboxItem
            key={merchant.merchantId}
            merchant={merchant}
            isChecked={selectedMerchantIds.has(merchant.merchantId)}
            onSelectionChange={handleMerchantSelectionChange}
          />
        ))}
      </Stack>
    );
  }, [filteredMerchants, selectedMerchantIds, handleMerchantSelectionChange]);

  // Memoized search handler
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleDeleteClick = () => {
    onDeleteAlertOpen();
  };

  const handleDeleteConfirm = () => {
    onDeleteAlertClose();
    if (onDelete) {
      onDelete();
    }
  };

  // Ensure latest group name is flushed before submit
  const handleSubmitClick = useCallback(
    (e) => {
      const name = groupNameRef.current?.value ?? '';
      flushSync(() => {
        setGroupModalData((prev) => ({ ...prev, name }));
      });
      onSubmit(e);
    },
    [onSubmit, setGroupModalData],
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent w={{ base: 'calc(100% - 24px)', md: '100%' }}>
          <ModalHeader>
            {groupModalData.isEdit ? 'Edit Group' : 'Create New Group'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb="6" px={{ base: '14px', md: '16px' }}>
            <VStack spacing="4" align="stretch">
              <FormControl isRequired>
                <FormLabel
                  htmlFor="group-name-input"
                  fontSize="sm"
                  fontWeight="medium"
                >
                  Group Name
                </FormLabel>
                <Input
                  id="group-name-input"
                  placeholder="Enter group name..."
                  ref={groupNameRef}
                  defaultValue={groupModalData.name || ''}
                  onBlur={handleGroupNameBlur}
                  borderRadius="8px"
                  _focus={{
                    borderColor: '#6658fd',
                    boxShadow: 'none',
                  }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel
                  htmlFor="merchant-search-input"
                  fontSize="sm"
                  fontWeight="medium"
                >
                  Select Merchants
                </FormLabel>

                {/* Search Box */}
                <InputGroup size="sm" mb="3">
                  <InputLeftElement pointerEvents="none">
                    <FaSearch color="#9CA3AF" size="12px" />
                  </InputLeftElement>
                  <Input
                    id="merchant-search-input"
                    placeholder="Search merchants..."
                    onChange={handleSearchChange}
                    borderRadius="6px"
                    fontSize="0.875rem"
                    _focus={{
                      borderColor: '#3182ce',
                      boxShadow: 'none',
                    }}
                  />
                </InputGroup>

                <Box
                  border="1px solid #E2E8F0"
                  borderRadius="8px"
                  maxH="300px"
                  overflowY="auto"
                  overflowX="hidden"
                >
                  {filteredMerchants.length > 0 ? (
                    merchantListContent
                  ) : (
                    <Box p="4" textAlign="center">
                      <Text fontSize="0.875rem" color="gray.500">
                        {searchTerm
                          ? 'No merchants found'
                          : 'No merchants available'}
                      </Text>
                    </Box>
                  )}
                </Box>
                {groupModalData.merchants.length > 0 && (
                  <Text fontSize="0.75rem" color="gray.600" mt="2">
                    {groupModalData.merchants.length} merchant(s) selected
                  </Text>
                )}
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter px={{ base: '14px', md: '16px' }}>
            <HStack spacing="3" w="100%" justify="space-between">
              <Box>
                {groupModalData.isEdit && onDelete && (
                  <Button
                    colorScheme="red"
                    onClick={handleDeleteClick}
                    isLoading={isDeleting}
                    loadingText="Deleting..."
                    isDisabled={
                      isCreatingGroup || isUpdatingGroup || isDeleting
                    }
                    fontSize={{ base: '12px', md: '14px' }}
                    fontWeight="500"
                    px={{ base: '14px', md: '16px' }}
                  >
                    Delete Group
                  </Button>
                )}
              </Box>

              {/* Cancel and Save buttons on the right */}
              <HStack justify="flex-end">
                <Button
                  variant="ghost"
                  onClick={onCancel}
                  isDisabled={isCreatingGroup || isUpdatingGroup || isDeleting}
                  fontSize={{ base: '12px', md: '14px' }}
                  fontWeight="500"
                  px={{ base: '5px', md: '16px' }}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="blue"
                  onClick={handleSubmitClick}
                  isLoading={
                    groupModalData.isEdit ? isUpdatingGroup : isCreatingGroup
                  }
                  loadingText={
                    groupModalData.isEdit ? 'Updating...' : 'Creating...'
                  }
                  isDisabled={isCreatingGroup || isUpdatingGroup || isDeleting}
                  fontSize={{ base: '12px', md: '14px' }}
                  fontWeight="500"
                  px={{ base: '14px', md: '16px' }}
                >
                  {groupModalData.isEdit ? 'Update Group' : 'Create Group'}
                </Button>
              </HStack>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteAlertOpen}
        isCentered
        leastDestructiveRef={cancelRef}
        onClose={onDeleteAlertClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Group
            </AlertDialogHeader>

            <AlertDialogBody>
              {`Are you sure you want to delete the group "${groupModalData.name}"? This action cannot be undone.`}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={isDeleting}
                loadingText="Deleting..."
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

GroupManagementModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  groupModalData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    merchants: PropTypes.array.isRequired,
    isEdit: PropTypes.bool.isRequired,
    editId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  setGroupModalData: PropTypes.func.isRequired,
  merchantNameList: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isCreatingGroup: PropTypes.bool,
  isUpdatingGroup: PropTypes.bool,
  isDeleting: PropTypes.bool,
  onMerchantSelection: PropTypes.func.isRequired,
};

export default GroupManagementModal;
